import { Link } from 'react-router-dom';
import { Mail, MapPin, Instagram, Linkedin, Youtube, Facebook } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0C0C0C] border-t border-[#27272A]" data-testid="footer">
      <div className="container-wide py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block text-2xl font-bold tracking-tight mb-6">
              <span className="text-white">TERRY</span>
              <span className="text-[#007AFF]">WEBSTER</span>
            </Link>
            <p className="text-[#A1A1AA] max-w-md leading-relaxed mb-6">
              Australian filmmaker, cinematographer, and producer with 17 years of experience 
              crafting compelling visual stories for brands, documentaries, and humanitarian causes.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/tezzawebster"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 border border-[#27272A] hover:border-[#007AFF] hover:text-[#007AFF] transition-colors"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/tezzawebster711"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 border border-[#27272A] hover:border-[#007AFF] hover:text-[#007AFF] transition-colors"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.linkedin.com/in/terry-webster"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 border border-[#27272A] hover:border-[#007AFF] hover:text-[#007AFF] transition-colors"
                data-testid="social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://www.youtube.com/@terrywebster9"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 border border-[#27272A] hover:border-[#007AFF] hover:text-[#007AFF] transition-colors"
                data-testid="social-youtube"
                aria-label="YouTube"
              >
                <Youtube size={20} />
              </a>
              <a
                href="https://www.tiktok.com/@tezza.webster"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 border border-[#27272A] hover:border-[#007AFF] hover:text-[#007AFF] transition-colors"
                data-testid="social-tiktok"
                aria-label="TikTok"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#007AFF] font-bold mb-6">
              Navigation
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/commercial-work" className="text-[#A1A1AA] hover:text-white transition-colors">
                  Commercial Work
                </Link>
              </li>
              <li>
                <Link to="/films-for-humanity" className="text-[#A1A1AA] hover:text-white transition-colors">
                  Real Human Stories
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-[#A1A1AA] hover:text-white transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-[#A1A1AA] hover:text-white transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#A1A1AA] hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#007AFF] font-bold mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/films-that-fill-rooms"
                  className="text-[#A1A1AA] hover:text-white transition-colors inline-flex items-center gap-1 group"
                  data-testid="footer-resorts-link"
                >
                  For Resorts
                  <span className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">→</span>
                </Link>
              </li>
              <li>
                <Link to="/commercial-work" className="text-[#A1A1AA] hover:text-white transition-colors">
                  Brand Films
                </Link>
              </li>
              <li>
                <Link to="/films-for-humanity" className="text-[#A1A1AA] hover:text-white transition-colors">
                  Documentary
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#007AFF] font-bold mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#007AFF] mt-1 flex-shrink-0" />
                <span className="text-[#A1A1AA]">
                  Sydney, Australia<br />
                  Available Worldwide
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#007AFF] flex-shrink-0" />
                <a 
                  href="mailto:email@terrywebster.com.au" 
                  className="text-[#A1A1AA] hover:text-white transition-colors"
                >
                  email@terrywebster.com.au
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#27272A] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#52525B] text-sm">
            &copy; {currentYear} Terry Webster. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-[#52525B] hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#52525B] hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
